"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { formatCompactMonthDayRange } from "@/app/[lang]/[workspaceId]/_date/compact-month-day-range";

// dayjsプラグインを有効化（ISO週、月曜始まり）
dayjs.extend(isoWeek);

type WeekCellProps = {
  week: string | null | undefined;
  showRange?: boolean;
};

/**
 * 週の開始日から月内の週番号を計算する
 * @param weekStartDate - YYYY-MM-DD形式の週開始日（月曜日）
 * @returns 月内の週番号（1-5）
 */
function getWeekOfMonth(weekStartDate: string): number {
  const weekStart = dayjs(weekStartDate);
  const monthStart = weekStart.startOf("month");

  // その月の最初の月曜日を見つける
  let firstMonday = monthStart.isoWeekday(1);
  if (firstMonday.isBefore(monthStart)) {
    firstMonday = firstMonday.add(1, "week");
  }

  // 週番号を計算（最初の月曜日が第1週）
  if (weekStart.isBefore(firstMonday)) {
    return 1; // 月初の部分週
  }

  return Math.floor(weekStart.diff(firstMonday, "week")) + 1;
}

function formatWeekRange(
  weekStart: dayjs.Dayjs,
  weekEnd: dayjs.Dayjs,
  i18n: ReturnType<typeof useLingui>["i18n"],
): string {
  return formatCompactMonthDayRange(
    weekStart.toDate(),
    weekEnd.toDate(),
    i18n.locale,
  );
}

export const WeekCell = ({ week, showRange = true }: WeekCellProps) => {
  const { i18n } = useLingui();

  if (!week) {
    return <span className="whitespace-nowrap">-</span>;
  }

  try {
    const weekStart = dayjs(week);

    // 無効な日付の場合は元の値を表示
    if (!weekStart.isValid()) {
      return <span className="whitespace-nowrap">{week}</span>;
    }

    const weekEnd = weekStart.endOf("isoWeek"); // 週の終わり（日曜日）
    const weekNumber = getWeekOfMonth(week);
    const monthLabel = i18n.date(weekStart.toDate(), {
      year: "numeric",
      month: "short",
    });
    const weekLabel = i18n._(msg`Week ${weekNumber}, ${monthLabel}`);

    if (showRange) {
      const rangeLabel = formatWeekRange(weekStart, weekEnd, i18n);

      return (
        <span className="whitespace-nowrap">
          {i18n._(msg`${weekLabel} (${rangeLabel})`)}
        </span>
      );
    }

    return <span className="whitespace-nowrap">{weekLabel}</span>;
  } catch (_error) {
    // 無効な日付形式の場合は元の値を表示
    return <span className="whitespace-nowrap">{week}</span>;
  }
};
